import { z } from 'zod';
import { CareForecastSchema } from '../CareForecastSchema';
import { CareDocumentDataSchema } from '../CareDocumentDataSchema';
import { CareDocumentTreatmentDetailsSchema } from '../CareDocumentTreatmentDetailsSchema';
import { CareDocumentOvercomeChallengeSchema } from '../CareDocumentOvercomeChallengeSchema';
import { CareDocumentClinicalAndExperienceOutcomesSchema } from '../CareDocumentClinicalAndExperienceOutcomesSchema';
import { CareDocumentResourceSchema } from '../CareDocumentResourceSchema';
import { CareDocumentColumnSchema } from '../CareDocumentColumnSchema';
import { IObject } from '../../../IObject';
import { InitialAssessmentSummaryDataReferredProfessionalListSchema } from './InitialAssessmentSummaryDataReferredProfessionalListSchema';
import { CareDocumentWorkDetailsSchema } from '../CareDocumentWorkDetailsSchema';
import { requiresCaseReferenceNumber } from '../careDocumentChecks';
import {
  dateValidation,
  fullNameValidation,
  requiredString,
} from '../../../../validation';

export const InitialAssessmentSummaryDataSchema = CareDocumentDataSchema.extend(
  {
    patient: z.object({
      fullName: fullNameValidation('Patient name is required'),
      dob: dateValidation('Patient date of birth is required'),
      cancerType: requiredString('Cancer type is required'),
      dateOfDiagnosis: dateValidation('Date of diagnosis is required'),
      work: CareDocumentWorkDetailsSchema,
      level: z.string().optional(),
    }),
    perciStartDate: dateValidation('Perci Health start date is required'),
    gip: z.object({
      // Optional: L&G's GIP code. Aviva has none and uses policyNumber instead.
      code: z.string().optional(),
    }),
    policyNumber: z.string().optional(),

    dateOfConsultation: dateValidation('Date of consultation is required'),

    // The "Care Forecast" section — present only for organizations with the
    // careDocuments.careForecast setting on (Aviva, PPL-3295); when present it
    // is complete. The document rendering stays data-driven: the PDF shows the
    // fields only when the object is set, never by org checks. The create form
    // makes the object required when the setting is on.
    careForecast: CareForecastSchema.optional(),

    hna: z.array(CareDocumentColumnSchema),

    treatmentDetails: CareDocumentTreatmentDetailsSchema,

    planToOvercomeChallenges: z.array(CareDocumentOvercomeChallengeSchema),

    clinicalAndExperienceOutcomes:
      CareDocumentClinicalAndExperienceOutcomesSchema,

    professionalsReferred:
      InitialAssessmentSummaryDataReferredProfessionalListSchema,

    expertFullName: z.string().optional(),
    expertSummary: requiredString(
      'Cancer nurse specialist summary is required',
    ),

    signPosting: z.array(CareDocumentResourceSchema),
    communicationNotes: z.string().optional(),
  },
).check(requiresCaseReferenceNumber);

// The form's schema for an organization that captures a Care Forecast: the
// section is compulsory. safeExtend, not extend - zod refuses to overwrite a
// key of a schema carrying a check (PPL-4096).
export const InitialAssessmentSummaryDataWithCareForecastSchema =
  InitialAssessmentSummaryDataSchema.safeExtend({
    careForecast: z.object(
      CareForecastSchema.shape,
      'Care Forecast is required',
    ),
  });

export type InitialAssessmentSummaryData = z.infer<
  typeof InitialAssessmentSummaryDataSchema
>;
export type InitialAssessmentSummary =
  IObject.HasIdTimestamped<InitialAssessmentSummaryData>;
